import { portalText, usePortalTranslation } from "../../i18n/portal.js";
/**
 * `data-screen-label="Messaging"` — unit P11-06.
 *
 * The four media with their state, and the web-chat conversations beneath them.
 *
 * `coming_soon` OUTRANKS EVERYTHING. WhatsApp and Viber are waitlisted on WeTalk's side —
 * Meta Business messaging approval and a Viber service-account registration, neither of them
 * anything a customer can hurry — so their rows say so and their buttons are disabled rather
 * than leading to a form that cannot submit.
 *
 * The rows are drawn in the design's order — voice, the two waitlisted media, then web chat —
 * whatever order the API lists them in (ADR 0018).
 *
 * AMENDED 2026-10-09 (design sync, `WeTalk.dc.html` "Messaging"): Messaging is the TEXT media only —
 * voice is not listed here any more ("Phone calls are set up under Numbers & SIP", linked), the
 * "N channels live · …" kicker is gone, and the rows run the open media first, the waitlisted ones
 * after (`CHANNELS.filter(c => c.id !== 'voice').sort(soon last)`).
 *
 * AMENDED 2026-09-26 (plan-004 WC-81, FR-WC-15, FR-WC-20, review #175, #180): the conversation
 * table moved to `conversation-table.tsx` (handed to WC-90); a failed read of the media says what
 * failed, with the request id and Retry, instead of "Reading your channels…" forever; and the rows
 * stack at phone width so nothing inside the page is wider than it.
 */
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ApiError } from "@wetalk/api-client";
import { Button, Icon, Tag } from "@wetalk/ui";
import type { ReactNode } from "react";

import "./messaging.css";

import { ConversationTable } from "./conversation-table.js";
import { GLYPH } from "../../shell/glyph.js";
import { apiErrorText } from "../../api/api-error.js";
import { useGateway } from "../../api/provider.js";
import { messagingMediumListQuery } from "../../api/query.js";
import type { Medium, MediumState, MessagingMediumRowView } from "../../api/gateway.js";

export const MEDIUM_GLYPH = {
  voice: GLYPH.phoneCall,
  web_chat: GLYPH.browser,
  whatsapp: GLYPH.whatsappLogo,
  viber: GLYPH.chatCircleDots,
} as const satisfies Record<Medium, unknown>;

/** The words on the tag, exactly the three states the design draws. */
export const MEDIUM_STATE_LABEL = {
  get connected() { return portalText("operations", "connected_c2f9b7b4"); },
  get coming_soon() { return portalText("operations", "coming_soon_e4115be2"); },
  get not_connected() { return portalText("operations", "not_connected_8b02f3de"); },
} as const satisfies Record<MediumState, string>;

/** The design's tag classes: accent for a connected medium, neutral for the other two. */
export const MEDIUM_STATE_TONE = {
  connected: "accent",
  coming_soon: "neutral",
  not_connected: "neutral",
} as const satisfies Record<MediumState, "accent" | "neutral">;

/** The design's row order (WeTalk.dc.html, `CHANNELS`, voice left out, waitlisted media last). */
const MEDIUM_SORT_ORDER: readonly Medium[] = ["voice", "whatsapp", "viber", "web_chat"];

/** The rows Messaging lists: every medium but voice, open ones first, in the design's order. */
export function messagingRowsOf(rows: readonly MessagingMediumRowView[]): readonly MessagingMediumRowView[] {
  const soonLast = (row: MessagingMediumRowView): number => (row.state === "coming_soon" ? 1 : 0);
  return rows
    .filter((one) => one.medium !== "voice")
    .sort(
      (left, right) =>
        soonLast(left) - soonLast(right) ||
        MEDIUM_SORT_ORDER.indexOf(left.medium) - MEDIUM_SORT_ORDER.indexOf(right.medium),
    );
}

export function MessagingScreen(): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const media = useQuery(messagingMediumListQuery(gateway));

  if (media.isError) {
    const requestId = media.error instanceof ApiError ? media.error.requestId : null;
    return (
      <section className="c-page mg-page">
        <h1 className="mg-title">{portalText("operations", "messaging_caef62a0")}</h1>
        <div className="mg-problem" role="alert">
          <p className="mg-problem-title">{portalText("operations", "your_channels_could_not_be_read_d3436cdf")}</p>
          <p className="mg-card-note">
            {apiErrorText(media.error, portalText("operations", "the_channels_could_not_be_read_f3a4807a"))} {portalText("operations", "nothing_has_changed_9f5f5b8a")}{requestId === null ? null : (
              <>
                {" "}
                {portalText("operations", "request_id_cbe1c0a3")}<span className="mg-request-id">{requestId}</span>.
              </>
            )}
          </p>
          <div className="mg-card-actions">
            <Button
              variant="primary"
              disabled={media.isFetching}
              onClick={() => {
                void media.refetch();
              }}
            >
              {media.isFetching ? portalText("operations", "trying_again_4900bdd3") : portalText("operations", "retry_9f5cd8a2")}
            </Button>
          </div>
        </div>
      </section>
    );
  }

  if (media.data === undefined) {
    return (
      <section className="c-page">
        <p className="c-note" role="status">
          {portalText("operations", "reading_your_channels_5ec4b598")}</p>
      </section>
    );
  }

  const ordered = messagingRowsOf(media.data);

  return (
    <section className="c-page mg-page">
      <h1 className="mg-title">{portalText("operations", "messaging_caef62a0")}</h1>
      <p className="mg-lede">
        {portalText("operations", "text_conversations_with_your_agents_they_answer_from_the_s_eb91065d")}<Link to="/numbers">{portalText("operations", "numbers_sip_dad16cb4")}</Link>.
      </p>

      <ul className="mg-list">
        {ordered.map((one) => (
          <MediumRow key={one.medium} row={one} />
        ))}
      </ul>

      <ConversationTable />
    </section>
  );
}

function MediumRow({ row }: { readonly row: MessagingMediumRowView }): ReactNode {
  usePortalTranslation("operations");
  return (
    <li className="mg-row">
      <span className={row.state === "coming_soon" ? "mg-mark mg-mark-soon" : "mg-mark"}>
        <Icon glyph={MEDIUM_GLYPH[row.medium]} size="xl" />
      </span>
      <span className="mg-row-body">
        <span className={row.state === "coming_soon" ? "mg-row-name mg-row-name-soon" : "mg-row-name"}>
          {/* The name is a link in every state, including `coming_soon`: the detail screen
              is where the hold-up is explained, and a row you cannot open is a row that
              cannot answer "why not". The BUTTON still reads "Not yet available". */}
          <Link to="/messaging/$mediumCode" params={{ mediumCode: row.medium }}>
            {row.name}
          </Link>
          <Tag tone={MEDIUM_STATE_TONE[row.state]}>{MEDIUM_STATE_LABEL[row.state]}</Tag>
        </span>
        <span className="mg-row-dek">{row.dek}</span>
        <span className="mg-row-meta">
          {row.detail}
        </span>
      </span>
      {row.state === "coming_soon" ? (
        <Button variant="secondary" disabled>
          {portalText("operations", "not_yet_available_5a28f15d")}</Button>
      ) : (
        <Link
          to="/messaging/$mediumCode"
          params={{ mediumCode: row.medium }}
          className={row.state === "connected" ? "btn btn-secondary" : "btn btn-primary"}
        >
          {row.state === "connected"
            ? portalText("operations", "manage_bf58d17e")
            : row.medium === "web_chat"
              ? portalText("operations", "get_snippet_daf347f9")
              : portalText("operations", "set_up_a5041fda")}
        </Link>
      )}
    </li>
  );
}
